
import {
  Component,
  Input,
  ViewChild,
  AfterViewInit,
  inject,
  signal,
} from '@angular/core';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';
import { Pharmacy } from '@interfaces';
import { LoaderService } from '@management';
import { GoogleMapsService } from '@services';
import { take, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-google-map',
  standalone: true,
  imports: [GoogleMapsModule],
  template: `
    @if (googleMapLoaded()) {
      <div>
        <google-map
          #googleMap
          [zoom]="14"
          [center]="{ lat: lat(), lng: lng() }"
          [options]="{ disableDefaultUI: true, mapId: mapId }"
          style="width: 100%; height: 100%"
        ></google-map>
      </div>
    }
    `,
  styles: [],
})
export class GoogleMapsComponent implements AfterViewInit {
  @Input() public pharmacy: Pharmacy;
  @ViewChild('googleMap', { static: false }) googleMap?: GoogleMap;

  public readonly lat = signal(0);
  public readonly lng = signal(0);
  public readonly googleMapLoaded = signal(false);

  public readonly mapId: string = environment.googleMapId;
  private readonly googleMapService = inject(GoogleMapsService);
  private readonly loader = inject(LoaderService);
  private placeId!: string;

  ngAfterViewInit(): void {
    this.loadMap();
  }

  private loadMap(): void {
    this.googleMapService
      .loadGoogleMapsScript()
      .pipe(
        take(1),
        tap(() => {
          this.googleMapLoaded.set(true);
        })
      )
      .subscribe(() => {
        this.setMarkers();
      });
  }

  private setMarkers(): void {
    if (typeof google === 'object' && typeof google.maps === 'object') {
      const address = `${this.pharmacy.name} ${this.pharmacy.address.zipCode} ${this.pharmacy.address.city} ${this.pharmacy.address.state} ${this.pharmacy.address.addressLine1} ${this.pharmacy.address.addressLine2}`;

      this.googleMapService
        .getCoordinates(address)
        .pipe(take(1))
        .subscribe(
          (results) => {
            this.placeId = results[0].place_id;
            this.lat.set(results[0].geometry.location.lat);
            this.lng.set(results[0].geometry.location.lng);
            this.addAdvancedMarker();
            this.loader.hideLoader();
          },
          () => {
            console.error('Geocoding failed');
            this.loader.hideLoader();
          }
        );
    } else {
      console.error('Google Maps API is not available');
      this.loader.hideLoader();
    }
  }

  private addAdvancedMarker(): void {
    const map = this.googleMap?.googleMap;
    const lat = this.lat();
    const lng = this.lng();

    if (map && lat && lng) {
      const markerLatLng = new google.maps.LatLng(lat, lng);
      map.setCenter(markerLatLng);
      map.setZoom(15);

      const pinImage = document.createElement('img');
      pinImage.src = this.pharmacy.inNetwork
        ? '../../../../assets/images/pin_red.png'
        : '../../../../assets/images/pin_blue.png';

      const advancedMarker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat, lng },
        map,
        content: pinImage,
      });

      advancedMarker.addListener('gmp-click', () => this.openMap());
    } else {
      console.error('Google map or coordinates are not ready.');
    }
  }

  public openMap(): void {
    if (this.placeId) {
      window.open(
        `https://www.google.com/maps/place/?q=place_id:${this.placeId}`,
        '_blank'
      );
    } else {
      console.error('Place ID is not available');
    }
  }
}
